// =============================
// ÍNDICE DO ARQUIVO
// 1: Importações
// 13: Tipos e Props
// 23: Componente AddUserAdminDialog
// 27: useState e useEffect
// 34: handleSave
// 67: JSX do Dialog
// =============================
import React, { useState } from 'react';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl 
} from '@mui/material';
import { supabaseAdminClient } from '../../Supabase/supabaseRealtimeClient';
import { sendInvite } from '../../Supabase/supabaseClient';

// Configurações do Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
const serviceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;



// Função para criar administrador
async function criarAdminUser({ user_id, nome, email, status = 'Ativo', perfil }: {
  user_id: string;
  nome: string;
  email: string;
  status?: string;
  perfil?: string;
}): Promise<boolean> {
  if (!supabaseUrl || !serviceKey) {
    throw new Error('Variáveis de ambiente do Supabase não definidas');
  }
  const body = { user_id, nome, email, status, perfil };
  const response = await fetch(`${supabaseUrl}/rest/v1/administrador`, {
    method: 'POST',
    headers: {
      'apikey': serviceKey,
      'Authorization': `Bearer ${serviceKey}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal'
    },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error_description || data.message || `Erro ${response.status}`);
  }
  return true;
}

// Função para recuperação de senha
async function recoverPassword(email: string) {
  if (!supabaseUrl || !serviceKey) throw new Error('REACT_APP_SUPABASE_URL ou SERVICE_KEY não definida no .env');
  const response = await fetch(`${supabaseUrl}/auth/v1/recover`, {
    method: 'POST',
    headers: {
      'apikey': serviceKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error_description || data.message || `Erro ${response.status}`);
  }
  return data;
}

// Função para verificar se administrador já existe
async function verificarAdminExistente(email: string) {
  if (!supabaseUrl || !serviceKey) {
    throw new Error('Variáveis de ambiente do Supabase não definidas');
  }
  const response = await fetch(`${supabaseUrl}/rest/v1/administrador?email=eq.${encodeURIComponent(email)}`, {
    method: 'GET',
    headers: {
      'apikey': serviceKey,
      'Authorization': `Bearer ${serviceKey}`,
      'Content-Type': 'application/json'
    }
  });
  if (!response.ok) {
    throw new Error('Erro ao verificar administrador existente');
  }
  const data = await response.json();
  return data.length > 0 ? data[0] : null;
}

interface AddUserAdminDialogProps {
  open: boolean;
  onClose: () => void;
  initialData?: any;
  onSave?: (data: any) => Promise<void>;
  atualizarLista?: () => Promise<void>;
}

const AddUserAdminDialog: React.FC<AddUserAdminDialogProps> = ({ open, onClose, initialData, onSave, atualizarLista }) => {
  const [nome, setNome] = useState(initialData?.nome || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [password, setPassword] = useState('');
  const [perfil, setPerfil] = useState(initialData?.perfil || 'Administrador');
  const [emailError, setEmailError] = useState('');

  React.useEffect(() => {
    setNome(initialData?.nome || '');
    setEmail(initialData?.email || '');
    setPassword('');
  }, [initialData, open]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSave = async () => {
    if (!email || !nome || !perfil) {
      alert('Por favor, preencha todos os campos.');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Email inválido');
      return;
    }
    setEmailError('');
    if (onSave) {
      await onSave({ nome, email, perfil });
      if (atualizarLista) await atualizarLista();
      onClose();
      return;
    }

    try {
      const trimmedEmail = email.trim();
      const registrationPassword = password;

      if (!initialData && !registrationPassword) {
        alert('A senha é obrigatória para novos usuários.');
        return;
      }

      // Verificar se o administrador já existe
      const adminExistente = await verificarAdminExistente(trimmedEmail);
      
      if (adminExistente) {
        alert('Este e-mail já está em uso.');
        onClose();
        return;
      }

      // 1. Criar novo usuário no Auth e no Banco via Edge Function (Atômico)
      console.log('[AddUserAdminDialog] Criando novo usuário e registro via Function');
      const tableData = {
        nome,
        email: trimmedEmail,
        perfil,
        status: 'Ativo'
      };

      const { user } = await sendInvite(trimmedEmail, registrationPassword, 'administrador', tableData);
      if (!user) throw new Error('Falha ao criar administrador via Function.');

      if (atualizarLista) await atualizarLista();
      alert('Administrador criado com sucesso!');
      onClose();    } catch (error: any) {
      console.error('Erro ao enviar convite ou criar administrador:', error);
      alert(`Erro: ${error.message || 'Tente novamente.'}`);
    }
  };

  const isEditing = !!initialData;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEditing ? 'Editar Usuário Administrativo' : 'Adicionar Novo Usuário Administrativo'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isEditing ? 'Atualize as informações do usuário abaixo.' : 'Preencha as informações abaixo para adicionar um novo usuário administrativo.'}
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="nome"
          label="Nome Completo"
          type="text"
          fullWidth
          variant="outlined"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          required
        />
        <TextField
          margin="dense"
          id="email"
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (!validateEmail(e.target.value)) {
              setEmailError('Email inválido');
            } else {
              setEmailError('');
            }
          }}
          required
          error={!!emailError}
          helperText={emailError}
        />
        {!initialData && (
          <TextField
            margin="normal"
            label="Senha"
            type="password"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
        <FormControl fullWidth margin="dense">
          <InputLabel id="perfil-label">Perfil</InputLabel>
          <Select
            labelId="perfil-label"
            id="perfil"
            value={perfil}
            label="Perfil"
            onChange={(e) => setPerfil(e.target.value)}
          >
            <MenuItem value="Administrador">Administrador</MenuItem>
            <MenuItem value="Suporte">Suporte</MenuItem>
            <MenuItem value="Financeiro">Financeiro</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserAdminDialog;
